import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';

import { inventoriesActions } from '../../../behavior/inventories/index';
import InventoryDetailsForm from './InventoryDetailsForm';
import getInventoryDetailsFormSchema from '../../../formSchemas/InventoryDetailsFormSchema';
import GenericDetails from '../../common/GenericDetails';
import {
  CLONE_MODE,
  CREATE_MODE,
  entityType,
  UNDO_DELETE_MODE,
  EDIT_MODE,
} from '../../../constants';
import { genericDetailsActions } from '../../../behavior/genericDetails';
import { manipulateValuesBeforeSubmission } from '../../../utils';
import useUser from '@behavior/init/getUserV2';
import { PersonDTO } from '@behavior/inventories/dto/PersonDTO';

type Props = {
  save: any;
  mode: string;
  isLoading: boolean;
  initialData: any;
  inventory: any;
  deleteInventory: any;
  id: number;
  getInitialData: (arg1: boolean, arg2: boolean) => void;
  getInventory: (arg1: number, arg2: boolean) => void;
  getDeletedInventory: (arg1: number) => void;
  toggleValidationMessage: () => void;
  processedNotificationBody: () => void;
  recipients: PersonDTO[];
  markAsChecked: boolean;
};
const InventoryDetailsFormContainer = ({
  save,
  mode,
  isLoading,
  initialData,
  inventory,
  deleteInventory,
  id,
  getInitialData,
  getInventory,
  getDeletedInventory,
  toggleValidationMessage,
  processedNotificationBody,
  recipients,
  markAsChecked,
}: Props) => {
  const { i18n } = useTranslation();
  const { user } = useUser();
  const chemProdId = new URLSearchParams(window.location.search).get('chemProdId');
  const [currentStep, setCurrentStep] = useState(mode === CREATE_MODE && !chemProdId ? 1 : 2);

  useEffect(() => {
    getInitialData(mode == CREATE_MODE || mode == CLONE_MODE, mode == CLONE_MODE);
    if (id && mode == UNDO_DELETE_MODE) getDeletedInventory(id);
    else if (id) getInventory(id, mode == CLONE_MODE);
  }, []);

  const validateAndSetCurrentStep = (selectedStep: number, validateForm: any) => {
    if (currentStep === 1 && selectedStep > 1) {
      validateForm().then((errors: any) => {
        if (!errors || !errors.chemicalProduct) setCurrentStep(selectedStep);
        else toggleValidationMessage();
      });
    } else {
      setCurrentStep(selectedStep);
    }
  };

  const submitHandle = (values: any, actions: any) => {
    save(
      manipulateValuesBeforeSubmission(
        values,
        processedNotificationBody,
        recipients,
        user,
        i18n,
        markAsChecked
      ),
      mode
    );
    actions.setSubmitting(false);
  };

  const invDetailsForm = (props: any) => <InventoryDetailsForm {...props} isHistory={false} />;
  return (
    <GenericDetails
      initialValues={inventory}
      validationSchema={getInventoryDetailsFormSchema()}
      initialData={initialData}
      save={save}
      mode={mode}
      isLoading={isLoading}
      isHistory={false}
      id={id}
      entityTypeProp={entityType.inventory}
      updateUser={inventory ? inventory.updateUser : null}
      deleteHandler={deleteInventory}
      renderContainer={invDetailsForm}
      currentStep={currentStep}
      validateAndSetCurrentStep={validateAndSetCurrentStep}
      validateOnChange={mode === CREATE_MODE || CLONE_MODE || EDIT_MODE}
      submitHandle={submitHandle}
    />
  );
};

const mapStateToProps = (state: any, ownProps: any) => ({
  ...state.inventories,
  mode: ownProps.match.params.mode,
  id: ownProps.match.params.id,
  processedNotificationBody: state.changesNotificationModal.processedChangesNotification,
  recipients: state.changesNotificationModal.recipients,
  markAsChecked: state.changesNotificationModal.markAsChecked,
});

const mapDispatchToProps = (dispatch: any) => ({
  getInventory: (id: number, isCloneMode: boolean) =>
    dispatch(inventoriesActions.getInventory(id, isCloneMode)),
  getDeletedInventory: (id: number) => dispatch(inventoriesActions.getDeletedInventory(id)),
  deleteInventory: (id: number) => dispatch(inventoriesActions.deleteInventory(id, true)),
  getInitialData: (isCreateMode: boolean, isCloneMode: boolean) =>
    dispatch(inventoriesActions.getInitialData(isCreateMode, isCloneMode)),
  save: (saveDto: any, mode: string) => dispatch(inventoriesActions.saveInventory(saveDto, mode)),
  toggleValidationMessage: () => dispatch(genericDetailsActions.toggleValidationMessage()),
});

InventoryDetailsFormContainer.defaultProps = {
  initialData: {},
  inventory: null,
};

export default React.memo(
  compose(connect(mapStateToProps, mapDispatchToProps))(InventoryDetailsFormContainer)
);
